/* eslint-disable no-console */
import { Component, OnInit } from '@angular/core';
import { SecurityService, DwError } from '@ng-darwin/security';
import { LoggerService } from '@ng-darwin/logger';
import { Constants } from './models/constants.class';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '@ng-darwin/config';
import { TealiumService } from './services/tealium.service';
import { WindowMessagesService } from './services/window-messages.service';
import { getCookie } from './helpers/utils';

/**
 * @description Component
 * @class AppComponent
 * @implements {OnInit} OnInit
 */
@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  title = 'Darwin para Angular';
  countDown = 0;
  docRoutes: { dw: string; jsonServer: string; ng: string; ngCli: string } = {
    dw: '',
    jsonServer: '',
    ng: '',
    ngCli: '',
  };
  response: any;
  public entityCode: string | null = '';
  public entityType: string | null = '';
  public infiniteLoop: any;
  public constants = Constants;
  public refreshError = false;
  public webviewTokenFallback = false;
  public isSoftCustomer = false;

  /**
   * @description Creates an instance of AppComponent.
   * @param securityService SecurityService
   * @param _loggerService LoggerService
   * @param _configService ConfigService
   * @param _translateService TranslateService
   * @param _tealiumService TealiumService
   * @param _windowMessagesService WindowMessagesService
   */
  constructor(
    public readonly securityService: SecurityService,
    private readonly _loggerService: LoggerService,
    private readonly _configService: ConfigService,
    private readonly _translateService: TranslateService,
    private readonly _tealiumService: TealiumService,
    private readonly _windowMessagesService: WindowMessagesService,
  ) {
    this._tealiumService.load();
    this.docRoutes = this._configService.config.app['docRoutes'] as any;
    const entryParams = getCookie(Constants.entryParamsCookieName);
    const queryParams = window.location.search
      ? window.location.search.split('?')[1]
      : '';
    // Avoid overwriting existing params when reloading from callback-login with only token
    const isCallbackReload =
      queryParams &&
      queryParams.split('&').length === 1 &&
      queryParams.startsWith('token=');
    if (queryParams && !isCallbackReload) {
      // entra por GET con queryparams
      this.saveParamsOnSessionStore(queryParams);
    } else if (queryParams && isCallbackReload) {
      // callback from bank login in webview
      const pair = queryParams.split('=');
      sessionStorage.setItem('_dwtk', pair[1]);
    } else if (entryParams) {
      // entra por POST con bodyparams
      this.saveParamsOnSessionStore(entryParams);
    }
    this.isSoftCustomer =
      sessionStorage.getItem('app-unique-id') ===
      this._configService.config.app['cdncApplication'];
    this.setLanguage(sessionStorage.getItem('language') as string);
  }

  /**
   * @description ngOnInit - Initialize the security service and the subscriptions to security, logger, and error events.
   * @returns Promise<void>
   */
  async ngOnInit(): Promise<void> {
    // Subscriptions to log events of Darwin modules. They can be deleted if you are not interested in.
    await this.initializeOrSkipSecurityModule();
    this._subscribeToLogEvents();
    // LINES BELOW ARE MANDATORY to init the session lifecycle and subscribe to important Darwin modules events:
    this._subscribeToErrorEvents();
    this._subscribeToSessionEvents();
    sessionStorage.removeItem('selectEntitiesBanks');
    sessionStorage.removeItem('currentAggregatedEntities');
    sessionStorage.removeItem('retryId');
    sessionStorage.removeItem('retryIndex');
    sessionStorage.removeItem('retry');
  }

  /**
   * @description initializeOrSkipSecurityModule
   * @returns Promise<void>
   */
  async initializeOrSkipSecurityModule(): Promise<void> {
    try {
      if (this.isSoftCustomer) {
        (window as any).utag_data.site_hierarchy = 'nocliente';
      } else {
        this._clearStaleCookies();
        await this.securityService.initialize();
        sessionStorage.setItem('_dwtk', this.securityService.token as string);
        const parsedToken = atob(this.securityService.token as string).split(
          '#',
        );
        const config = this._configService.config.app[
          'companyUserTypes'
        ] as string[];
        config.forEach((type) => {
          if (parsedToken.some((t) => t === type)) {
            sessionStorage.setItem('isCompanyUser', 'true');
            (window as any).utag_data.site_hierarchy = 'empresas';
          } else {
            (window as any).utag_data.site_hierarchy = 'particulares';
          }
        });
      }
      this.checkSession();
    } catch (err) {
      const isWebview = sessionStorage.getItem('iswebview') === 'true';
      const existingToken = sessionStorage.getItem('_dwtk');

      if (isWebview && existingToken) {
        // In webview mode, security refresh fails because cross-domain
        // navigation to the bank clears session cookies. Fall back to the
        // token that was restored from localStorage backup by callback-login.
        this.webviewTokenFallback = true;
        this._setUserTypeFromToken(existingToken);
        this.checkSession();
      } else {
        // capture refresh error
        this.refreshError = true;
        this._tealiumService.sendError({
          category: 'Invalid login token',
          action: 'warning',
          data: {
            cod_error: 400,
            desc_error: 'Refresh token service',
          },
        });
      }
    }
  }

  /**
   * @description Clear stale session cookies that may interfere with the token refresh.
   * Cookies cached from previous sessions on the domain can cause 403/401 errors
   * when the security module tries to refresh the token.
   */
  private _clearStaleCookies(): void {
    const cookieNames = document.cookie
      .split(';')
      .map((c) => c.trim().split('=')[0])
      .filter((name) => name.length > 0);

    const domains = ['', '.pru.bsch', 'trusso.pru.bsch'];

    cookieNames.forEach((name) => {
      domains.forEach((domain) => {
        const domainPart = domain ? `; domain=${domain}` : '';
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
      });
    });
  }

  /**
   * @description Parse an existing token and set user type hierarchy.
   * Used as fallback when securityService.initialize() cannot refresh the token
   * (e.g. in webview after cross-domain navigation clears cookies).
   */
  private _setUserTypeFromToken(token: string): void {
    try {
      const parsedToken = atob(token).split('#');
      const config = this._configService.config.app[
        'companyUserTypes'
      ] as string[];
      config.forEach((type) => {
        if (parsedToken.some((t) => t === type)) {
          sessionStorage.setItem('isCompanyUser', 'true');
          (window as any).utag_data.site_hierarchy = 'empresas';
        } else {
          (window as any).utag_data.site_hierarchy = 'particulares';
        }
      });
    } catch {
      (window as any).utag_data.site_hierarchy = 'particulares';
    }
  }

  /**
   *
   */
  checkSession(): void {
    this.infiniteLoop = setInterval(() => {
      this._windowMessagesService.session();
    }, 30000);
  }

  /**
   *
   */
  stopSession(): void {
    clearInterval(this.infiniteLoop);
  }

  /**
   * @description saveParamsOnSessionStore
   * @param event any
   */
  saveParamsOnSessionStore(params: string): void {
    const parsedParams = params.split('&');
    let pair: [string, any] = ['', null];
    const data: any = [];
    parsedParams.forEach((d: any) => {
      pair = d.split('=');
      if (pair[0] === 'language' && !pair[1]) {
        pair[1] = 'es-ES';
      }
      if (pair[0] === 'token') {
        pair[0] = '_dwtk';
      }
      sessionStorage.setItem(pair[0], pair[1]);
      if (pair[0] === 'iswebview' && pair[1] === 'true') {
        this._windowMessagesService.isWebview = true;
        data.push({
          name: 'n',
          value: 'offer_ext',
        });
        data.push({
          name: 'id',
          value: 'trusso',
        });
      }
      if (!Constants.internalAppParams.includes(pair[0])) {
        data.push({ name: pair[0], value: pair[1] });
      }
    });
    sessionStorage.setItem('params', JSON.stringify(data));
  }

  /**
   *
   */
  setLanguage(language: string): void {
    const appLanguages = [
      'es-ES',
      'ca-ES',
      'eu-ES',
      'gl-ES',
      'de-DE',
      'en-GB',
      'fr-FR',
      'it-IT',
      'pl-PL',
      'pt-PT',
    ];
    const languageToSelect =
      language && appLanguages.includes(language) ? language : 'es-ES';
    this._translateService.setDefaultLang(languageToSelect);
    if (!(window as any).utag_data) {
      (window as any).utag_data = {};
    }
    (window as any).utag_data.page_lang = languageToSelect;
  }

  /**
   * @description _subscribeToSessionEvents
   * @private
   */
  private _subscribeToSessionEvents(): void {
    // Do any logic your app needs with these events
    this.securityService.onSessionInitialized$.subscribe(() =>
      console.log('%conSessionInitialized', 'background: lightgreen'),
    );
    this.securityService.onSessionAboutToTimeout$.subscribe(() =>
      console.log('%conSessionAboutToTimeout', 'background: khaki'),
    );
    this.securityService.onSessionTimeout$.subscribe(() =>
      console.log('%conSessionTimeout', 'background: tomato'),
    );
    this.securityService.onSessionResume$.subscribe(() =>
      console.log('%conSessionResume', 'background: lightgreen'),
    );
    this.securityService.onSessionKilled$.subscribe(() =>
      console.log('%conSessionKilled', 'background: tomato'),
    );
  }

  /**
   * @description _subscribeToErrorEvents
   * @private
   */
  private _subscribeToErrorEvents(): void {
    // Darwin Security module errors
    this.securityService.onError$.subscribe((err: DwError) => {
      this._sendErrorToLogger(err);
    });

    // Darwin Logger module errors
    this._loggerService.onError$.subscribe((err: DwError) => {
      this._sendErrorToLogger(err);
    });
  }

  /**
   * @description _subscribeToLogEvents
   * @private
   */
  private _subscribeToLogEvents(): void {
    this.securityService.onLog$.subscribe((payload) =>
      console.log(
        'app.component.ts: Log event from Security module: ',
        payload,
      ),
    );
    this._loggerService.onLog$.subscribe((payload) =>
      console.log('app.component.ts: Log event from Logger module: ', payload),
    );
  }

  /**
   * @description _sendErrorToLogger
   * @private
   * @param error DwError
   * @returns Promise<void>
   */
  private async _sendErrorToLogger(error: DwError): Promise<void> {
    // you can delete console.error (just for logging in browser console)
    console.error('app.component.ts:', error);

    await this._loggerService.logError({
      component: 'AppComponent',
      log: `${error.name}: ${error.message}`,
    });
  }

  /**
   * @description exit
   */
  exit(): void {
    this._windowMessagesService.end();
  }
}
